import { FC, MutableRefObject, useContext, useMemo } from "react";
import APIContext from "../../contexts/APIContext";
import MedicallyReadyToggle from "../medically-ready-toggle";
import MedicallyReadyPill from "../medically-ready-pill";
import { MedicallyReadyStatusChoices } from "../../types/MedicallyReadyStatusChoices";
import { Typography } from "@analyticsMD/quil";
import { css } from "@emotion/css";
import { grey } from "@mui/material/colors";
import { Box } from "@analyticsMD/quil";
import moment from "moment/moment";

const styles = {
  medicallyReadyContainer: css`
    display: flex;
    width: 100%;
    justify-content: space-between;
  `,
  medicallyReady: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  `,
};

const sxStyles = {
  header: {
    marginLeft: "0.9rem",
    fontSize: "0.8rem",
  },
  timestamp: {
    fontSize: "0.8rem",
    color: grey[600],
  },
};

interface MedicallyReadySectionProps {
  updateRef?: MutableRefObject<Record<string, any>>;
  useSidebarView?: boolean;
}

const MedicallyReadySection: FC<MedicallyReadySectionProps> = ({
  updateRef,
  useSidebarView = false,
}) => {
  const {
    medicallyReadyStatusQuery,
    patientID,
    setShowErrorToast,
    configurationQuery,
    patientDataReadOnly,
  } = useContext(APIContext);

  const medicallyReadyStatus = medicallyReadyStatusQuery.isSuccess
    ? medicallyReadyStatusQuery.data
    : null;

  const configuration = configurationQuery?.data
    ? configurationQuery?.data?.[0]
    : {};

  const medicallyReadyStatusTimestamp = useMemo(() => {
    if (!medicallyReadyStatus?.qv_updated_ts) return null;
    const momentTime = moment(medicallyReadyStatus.qv_updated_ts);
    return momentTime.fromNow();
  }, [medicallyReadyStatus?.qv_updated_ts]);

  const isReadOnly =
    patientDataReadOnly || Boolean(configuration.medically_ready_read_only);

  const isMedicallyReady =
    medicallyReadyStatus?.status === MedicallyReadyStatusChoices.YES;

  // Only render the audit time when we actually have one (avoids an empty node
  // that still consumes the flex gap). In read-only mode, show it only when the
  // patient IS medically ready (ENG-30653); otherwise whenever a timestamp exists.
  const showTimestamp =
    Boolean(medicallyReadyStatusTimestamp) && (!isReadOnly || isMedicallyReady);

  const timestamp = showTimestamp && (
    <Typography sx={sxStyles.timestamp}>
      {medicallyReadyStatusTimestamp}
    </Typography>
  );

  return (
    <Box className={styles.medicallyReadyContainer}>
      <Typography
        sx={{
          ...sxStyles.header,
          marginLeft: "0.35rem",
          textWrap: "nowrap",
          display: "flex",
          alignItems: "center",
          fontSize: "0.9rem",
        }}
      >
        Medically ready:
      </Typography>
      <Box className={styles.medicallyReady}>
        {isReadOnly ? (
          <>
            <MedicallyReadyPill isMedicallyReady={isMedicallyReady} />
            {timestamp}
          </>
        ) : (
          <>
            {timestamp}
            <MedicallyReadyToggle
              medicallyReadyStatus={medicallyReadyStatus}
              patientID={patientID}
              setShowErrorToast={setShowErrorToast}
              updateRef={updateRef}
              useSidebarView={useSidebarView}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default MedicallyReadySection;
